import { z } from 'zod';
import { INSPECTION_STATUSES } from './inspection.js';
// H-Next: the general handover checks ("בדיקות ומסירה כללית": keys, remotes, parking gate, parking
// and storage, spare tiles, ...). They are part of the project's inspection template (Master Data);
// each handover keeps its own copy, with a result (the inspection results), an optional quantity
// and a note. The final handover's reinspection is in handover.ts.
export const GENERAL_CHECK_LIMITS = {
    name: 80,
    unit: 20,
    items: 200,
    note: 300,
    maxQuantity: 9999,
};
/** The starter general checks, after the prototype's "מסירה כללית", parking, storage and spare tiles. */
export const STARTER_GENERAL_CHECKS = [
    { name: 'מפתחות לדירה', unit: 'יח׳' },
    { name: 'מפתחות למחסן', unit: 'יח׳' },
    { name: 'שלטים לשער החניה', unit: 'יח׳' },
    { name: 'הסבר על שער החניון', unit: null },
    { name: 'תיבת דואר', unit: null },
    { name: 'חניה', unit: null },
    { name: 'מחסן', unit: null },
    { name: 'אריחי בדק – סלון', unit: 'יח׳' },
    { name: 'אריחי בדק – חדר רטוב', unit: 'יח׳' },
    { name: 'חיפוי בדק – חדר רטוב', unit: 'יח׳' },
    { name: 'אריחי בדק – מרפסת', unit: 'יח׳' },
];
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));
// ---- Template (Master Data) ------------------------------------------------------------------
export const generalCheckTemplateItemSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    /** The unit of the quantity counted (for example יח׳); null: no quantity. */
    quantityUnit: z.string().nullable(),
    active: z.boolean(),
    rowVersion: z.number().int().min(1),
})
    .strict();
/** `GET`/`PUT /projects/:id/general-checks`: every check in display order. */
export const generalChecksResponseSchema = z
    .object({
    listVersion: z.string().regex(/^[0-9a-f]{64}$/),
    /** The inspection template revision (shared with the checklist items). */
    revision: z.number().int().min(0),
    items: z.array(generalCheckTemplateItemSchema),
    permissions: z.object({ edit: z.boolean() }).strict(),
})
    .strict();
export const generalCheckItemInputSchema = z
    .object({
    id: z.uuid().nullable(),
    name: z.string().trim().min(1).max(GENERAL_CHECK_LIMITS.name),
    quantityUnit: optionalText(GENERAL_CHECK_LIMITS.unit),
    active: z.boolean(),
})
    .strict();
/**
 * `PUT /projects/:id/general-checks`: the whole list in display order. Every stored check must be
 * listed (checks are deactivated, never removed); a list changed meanwhile is refused.
 */
export const generalChecksSaveRequestSchema = z
    .object({
    listVersion: z.string().regex(/^[0-9a-f]{64}$/),
    items: z.array(generalCheckItemInputSchema).max(GENERAL_CHECK_LIMITS.items),
})
    .strict()
    .superRefine((request, context) => {
    const ids = new Set();
    const names = new Set();
    request.items.forEach((item, index) => {
        if (item.id !== null) {
            if (ids.has(item.id))
                context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'id'] });
            ids.add(item.id);
        }
        const name = item.name.toLowerCase();
        if (names.has(name))
            context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'name'] });
        names.add(name);
    });
});
// ---- A handover's general checks ---------------------------------------------------------------
/** One general check of a handover: the name and unit as they were when the handover copied them. */
export const handoverGeneralCheckSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    quantityUnit: z.string().nullable(),
    status: z.enum(INSPECTION_STATUSES),
    quantity: z.number().int().min(0).nullable(),
    note: z.string().nullable(),
})
    .strict();
export const handoverGeneralChecksSchema = z
    .object({
    /** The template revision the checks came from; null: the handover has none yet. */
    revision: z.number().int().min(0).nullable(),
    items: z.array(handoverGeneralCheckSchema),
})
    .strict();
export const generalCheckResultInputSchema = z
    .object({
    id: z.uuid(),
    status: z.enum(INSPECTION_STATUSES),
    quantity: z.number().int().min(0).max(GENERAL_CHECK_LIMITS.maxQuantity).nullable(),
    note: optionalText(GENERAL_CHECK_LIMITS.note),
})
    .strict();
//# sourceMappingURL=general-checks.js.map